import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  FileText, Upload, Eye, Download, Trash2, Plus, Camera, Image as ImgIcon,
} from "lucide-react";
import { fmtRoDate } from "../lib/dateFmt";
import AuditLine from "../components/AuditLine";

const FOLDER = "acte_firma";

const DOC_TYPES = [
  {
    code: "handelregister",
    title: "Handelsregisterauszug",
    icon: "📜",
    description: "Extras din Registrul Comerțului",
    color: "blue",
  },
  {
    code: "betriebshaftpflicht",
    title: "Betriebshaftpflichtversicherung",
    icon: "🛡️",
    description: "Asigurare răspundere civilă",
    color: "emerald",
  },
  {
    code: "mehrwertsteuer",
    title: "Mehrwertsteuernummer",
    icon: "🧾",
    description: "Cod TVA / Identificare fiscală",
    color: "violet",
  },
  {
    code: "betreibungsauszug",
    title: "Betreibungsauszug",
    icon: "📋",
    description: "Extras de urmărire / Datorii fiscale",
    color: "amber",
  },
];

const COLOR_CLASSES = {
  blue:    { card: "border-blue-300 bg-blue-50/40",   header: "text-blue-900",   accent: "bg-blue-600 hover:bg-blue-700",       light: "bg-blue-100 text-blue-800" },
  emerald: { card: "border-emerald-300 bg-emerald-50/40", header: "text-emerald-900", accent: "bg-emerald-600 hover:bg-emerald-700", light: "bg-emerald-100 text-emerald-800" },
  violet:  { card: "border-violet-300 bg-violet-50/40", header: "text-violet-900", accent: "bg-violet-600 hover:bg-violet-700",  light: "bg-violet-100 text-violet-800" },
  amber:   { card: "border-amber-300 bg-amber-50/40",  header: "text-amber-900",  accent: "bg-amber-600 hover:bg-amber-700",     light: "bg-amber-100 text-amber-800" },
};

export default function ActeFirma() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/documents", { params: { folder: FOLDER } });
      setDocs(r.data || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Doar adminii pot accesa această secțiune.");
      } else {
        toast.error("Eroare la încărcare");
      }
      setDocs([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="acte-firma-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-zinc-900 flex items-center gap-2.5">
            <FileText size={28} className="text-[var(--primary)]"/> Acte firmă
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Documentele oficiale ale firmei — încarcă scanate cu telefonul sau ca PDF. Acces doar pentru administratori.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-400 italic">Se încarcă documentele…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {DOC_TYPES.map((dt) => (
            <DocCategoryCard
              key={dt.code}
              type={dt}
              docs={docs.filter((d) => d.label === dt.code)}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DocCategoryCard({ type, docs, onChanged }) {
  const cls = COLOR_CLASSES[type.color] || COLOR_CLASSES.blue;
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`Fișier prea mare (${(file.size / 1024 / 1024).toFixed(1)}MB) — max 50MB.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", FOLDER);
      fd.append("label", type.code);
      await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${type.title} încărcat`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la upload");
    } finally { setUploading(false); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Șterge „${doc.original_filename}"?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Șters");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    }
  };

  // Sort: most recent first
  const sorted = [...docs].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const latest = sorted[0];

  return (
    <div
      className={`card-flat border-l-4 ${cls.card}`}
      data-testid={`doc-card-${type.code}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className={`font-display text-lg font-bold flex items-center gap-2 ${cls.header}`}>
              <span className="text-2xl">{type.icon}</span>
              <span className="truncate">{type.title}</span>
            </h3>
            <p className="text-[12px] text-zinc-600 mt-0.5">{type.description}</p>
            {latest && (
              <div className="mt-1.5 inline-flex items-center gap-1.5">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${cls.light}`}>
                  ✅ Cel mai recent
                </span>
                <span className="text-[11px] text-zinc-500">· {fmtRoDate(latest.created_at)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Upload buttons: camera (mobile) + file (PDF/image) */}
        <div className="flex flex-wrap gap-2 mt-3">
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handleUpload(e.target.files?.[0])}
            className="hidden"
            data-testid={`doc-camera-${type.code}`}
          />
          <Button
            size="sm"
            onClick={() => cameraRef.current?.click()}
            disabled={uploading}
            className={`${cls.accent} text-white`}
            data-testid={`doc-camera-btn-${type.code}`}
            title="Scanează cu camera telefonului"
          >
            <Camera size={14} className="mr-1.5"/> Scanează cu camera
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => handleUpload(e.target.files?.[0])}
            className="hidden"
            data-testid={`doc-file-${type.code}`}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`doc-file-btn-${type.code}`}
          >
            <Upload size={14} className="mr-1.5"/> Încarcă PDF/imagine
          </Button>
        </div>

        {/* List */}
        {sorted.length === 0 ? (
          <div className="mt-3 text-xs text-zinc-400 italic">Nicio versiune încărcată încă.</div>
        ) : (
          <div className="mt-3 space-y-1.5">
            <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">
              Versiuni încărcate ({sorted.length})
            </div>
            {sorted.map((d) => (
              <DocItemRow key={d.id} doc={d} onDelete={() => handleDelete(d)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DocItemRow({ doc, onDelete }) {
  const isImage = (doc.content_type || "").startsWith("image/");
  const ext = (doc.original_filename || "").split(".").pop().toLowerCase();
  const Icon = isImage ? ImgIcon : FileText;

  // Open in new tab for inline preview
  const viewUrl = `${api.defaults.baseURL}/documents/${doc.id}`;
  const downloadUrl = `${api.defaults.baseURL}/documents/${doc.id}?download=1`;

  return (
    <div className="flex items-center gap-2 px-2 py-2 bg-white border border-zinc-200 rounded text-[13px] hover:shadow-sm transition-shadow">
      <Icon size={16} className="text-zinc-500 shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{doc.original_filename}</div>
        <div className="text-[10px] text-zinc-500">
          {(doc.size / 1024).toFixed(0)}KB · <span className="uppercase">{ext}</span>
        </div>
        <AuditLine entry={doc} testid={`doc-audit-${doc.id}`} compact />
      </div>
      <a
        href={viewUrl}
        target="_blank"
        rel="noreferrer"
        className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-50 font-semibold"
        title="Vizualizează"
        data-testid={`doc-view-${doc.id}`}
      >
        <Eye size={12}/> Vezi
      </a>
      <a
        href={downloadUrl}
        target="_blank"
        rel="noreferrer"
        className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-50 font-semibold"
        title="Descarcă"
        data-testid={`doc-download-${doc.id}`}
      >
        <Download size={12}/>
      </a>
      <button
        onClick={onDelete}
        className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-red-300 text-red-700 bg-white hover:bg-red-50 font-semibold"
        title="Șterge"
        data-testid={`doc-delete-${doc.id}`}
      >
        <Trash2 size={12}/>
      </button>
    </div>
  );
}
